import type { Slots } from 'vue';
import type { BlockDefinition, BlockPrivateRootContext, BlockSerializedChild } from '#layers/block-engine/types/block';
import type { BlockRootContextController } from '#layers/block-engine/utils/block/api/root-context';
import { computed, defineComponent, h, inject, provide, useId, useTemplateRef, watch } from 'vue';
import { useBlockDebuggerStore } from '#layers/block-engine/stores/block-debugger';
import { getBlockConfig } from '#layers/block-engine/utils/block/api/config';
import { blockChildKey, blockContextKey } from '#layers/block-engine/utils/block/api/context';
import { setupBlockInjection } from '#layers/block-engine/utils/block/inject';
import { parentRenderIdKey } from '#layers/block-engine/utils/block/render';
import BlockErrorBoundary from './BlockErrorBoundary.vue';
import { blockPrivateRootContextFallbackKey, blockRootContextApiFallbackKey } from './BlockRenderShared';

interface ProvideBlockDefinitionRenderContextOptions {
	/** Current block definition rendered by this component. */
	getBlock: () => BlockDefinition;
	/** Public root context visible through `useBlockContext()`. */
	rootContextApi: BlockRootContextController;
	/** Private root context visible to nested block renders. */
	getPrivateRootContext: () => BlockPrivateRootContext;
	/** Event bridge used by blocks to emit on the root render. */
	emitOnRootBlock: (event: string, payload?: any) => void;
	/** Whether to preserve any parent serialized child instead of creating a fake child. */
	unwrapBlockDefinition: () => boolean;
	/** Label used in stale injection warnings during development. */
	staleIdWarningSource: string;
}

interface UseBlockDefinitionRenderSetupOptions extends ProvideBlockDefinitionRenderContextOptions {
	/** Props passed to the rendered block component. */
	getComponentProps: () => Record<string, any>;
	/** Props exposed to the block debugger. */
	getDebugProps: () => Record<string, any>;
	/** Slots forwarded to the rendered block component. */
	slots: Slots;
	/** Called when the rendered block component ref becomes available. */
	onBlockRef?: (value: any) => void;
	/** Whether this definition should render without a local error boundary. */
	skipErrorBoundary?: () => boolean;
}

interface CreateBlockDefinitionRenderComponentOptions {
	/** Static block definition used by the serialized child renderer. */
	block: BlockDefinition;
	/** Public root context visible through `useBlockContext()`. */
	rootContextApi: BlockRootContextController;
	/** Root block override for the serialized tree that owns this child. */
	getRootBlock: () => BlockPrivateRootContext['rootBlockOverride'];
	/** Event bridge used by blocks to emit on the root render. */
	emit: (event: string, payload?: any) => void;
	/** Whether this child should render without a local error boundary. */
	skipErrorBoundary: boolean;
	/** Whether to preserve the parent serialized child context. */
	unwrapBlockDefinition: boolean;
}

/**
 * Provides the injection context (`provide()` calls) shared by every renderer of a `BlockDefinition`.
 * Returns the render id pair so callers can wire the block debugger / parent linkage.
 */
export function provideBlockDefinitionRenderContext(options: ProvideBlockDefinitionRenderContextOptions) {
	const renderId = useId();
	const parentRenderId = inject(parentRenderIdKey, null);
	const wrappedRootChild = computed(() =>
		options.unwrapBlockDefinition() ? null : createWrappedRootChild(options.getBlock()),
	);

	provide(blockRootContextApiFallbackKey, options.rootContextApi);
	provide(blockPrivateRootContextFallbackKey, options.getPrivateRootContext);
	provide(parentRenderIdKey, renderId);

	provide(blockContextKey, {
		getBlockDefinition: options.getBlock,
		getRootBlock: () => options.getPrivateRootContext().rootBlockOverride ?? options.getBlock(),
		...options.rootContextApi,
		emitOnRootBlock: options.emitOnRootBlock,
	});

	if (wrappedRootChild.value) {
		provide(blockChildKey, () => {
			const child = wrappedRootChild.value;

			if (!child) {
				throw new Error(`Child with id "child" is unavailable for block ${options.getBlock().id}`);
			}

			return child;
		});
	}

	const config = getBlockConfig(options.getBlock());
	const initialBlockId = options.getBlock().id;

	if (config.provide && Object.keys(config.provide).length > 0) {
		// `setupBlockInjection()` calls `provide()`, so changed block ids need a
		// remount to avoid leaving descendants on stale injection keys.
		setupBlockInjection(options.getBlock());

		if (import.meta.dev) {
			watch(() => options.getBlock().id, (nextId) => {
				if (nextId && nextId !== initialBlockId) {
					console.warn(
						`[${options.staleIdWarningSource}] block.id changed from "${initialBlockId}" to "${nextId}" without remount. `
						+ 'Provided injection is stale; ensure the parent keys this component by blockId.',
					);
				}
			});
		}
	}

	return {
		renderId,
		parentRenderId,
	};
}

/**
 * Sets up shared render state for a `BlockDefinition` and returns its render function.
 */
export function useBlockDefinitionRenderSetup(options: UseBlockDefinitionRenderSetupOptions) {
	const blockRef = useTemplateRef<any>('blockRef');
	const { renderId, parentRenderId } = provideBlockDefinitionRenderContext(options);

	const blockDebuggerStore = useBlockDebuggerStore();

	blockDebuggerStore.registerBlock({
		renderId,
		parentRenderId,
		blockId: options.getBlock().id,
		getVariables() {
			return {};
		},
		getProps: options.getDebugProps,
	});

	watch(blockRef, (value) => {
		if (value) {
			options.onBlockRef?.(value);
		}
	});

	function renderBlock() {
		const componentProps = options.getComponentProps();
		componentProps.ref ??= 'blockRef';

		return h(options.getBlock().comp, componentProps, options.slots);
	}

	function renderBlockWithBoundary() {
		const block = options.getBlock();

		return options.skipErrorBoundary?.()
			? renderBlock()
			: h(BlockErrorBoundary, {
					name: block.name,
					icon: block.icon ?? undefined,
				}, renderBlock);
	}

	return {
		renderId,
		parentRenderId,
		blockRef,
		renderBlock: renderBlockWithBoundary,
	};
}

/**
 * Creates a serialized-tree wrapper for child nodes backed by a `BlockDefinition`.
 */
export function createBlockDefinitionRenderComponent(options: CreateBlockDefinitionRenderComponentOptions) {
	const {
		block,
		getRootBlock,
		emit,
		skipErrorBoundary,
		unwrapBlockDefinition,
	} = options;

	return defineComponent({
		name: `BlockWrapper_${block.name}`,
		inheritAttrs: false,
		setup(_props, { attrs, slots, expose }) {
			const blockComponentStyle = computed(() => ({
				'--block-spacing': '8px',
			}));
			const blockComponentProps = computed(() => ({
				...attrs,
				'data-block-id': block.id,
				'style': blockComponentStyle.value,
			}));
			const { blockRef, renderBlock } = useBlockDefinitionRenderSetup({
				getBlock: () => block,
				getComponentProps: () => blockComponentProps.value,
				getDebugProps: () => ({
					...attrs,
				}),
				slots,
				rootContextApi: options.rootContextApi,
				getPrivateRootContext: () => ({
					rootBlockOverride: getRootBlock(),
				}),
				emitOnRootBlock: emit,
				unwrapBlockDefinition: () => unwrapBlockDefinition,
				skipErrorBoundary: () => skipErrorBoundary,
				staleIdWarningSource: 'BlockDefinitionRender',
			});

			expose({
				blockRef,
			});

			return renderBlock;
		},
	});
}

/**
 * Recreates the synthetic child shape that wrapped component blocks receive.
 */
function createWrappedRootChild(block: BlockDefinition): BlockSerializedChild {
	const slots: Record<string, string[]> = {};

	for (const slotDef of block.slots ?? []) {
		slots[slotDef.name] = [`slot-outlet-${slotDef.name}`];
	}

	return {
		id: 'child',
		blockId: block.id,
		props: {},
		slots,
	};
}
